import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@app/components/ui/tabs";
import { ExperimentStatusBadge } from "@app/components/ExperimentStatusBadge";
import { RunsTable, type ExperimentRun } from "@app/components/RunsTable";
import type { GroundingField } from "@app/components/DocumentGroundingViewer";
import { FeedbackDialog } from "@app/components/FeedbackDialog";
import { ValidationDialog } from "@app/components/ValidationDialog";
import {
  ExperimentMetricsPanel,
  type ExperimentMetrics,
} from "@app/components/ExperimentMetricsPanel";
import { Badge } from "@app/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@app/components/ui/radio-group";
import { Label } from "@app/components/ui/label";
import DatasetStateBadge from "@app/components/DatasetStateBadge";
import { Play, Square, BarChart3, Brain, Cpu, Network, Gauge, Lock } from "lucide-react";
import API from "@app/services/api";
import { useLabReadOnly } from "@app/utils/use-lab-read-only";
import { readRefusal, type DatasetState } from "@app/utils/reference-dataset";
import { Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";

/** The tabs a link may open directly (`?tab=metrics`) — the guided tour does. */
const EXPERIMENT_TABS = ["results", "metrics", "annotate", "tools"] as const;

interface Experiment {
  id: string;
  name: string;
  description: string | null;
  status: string;
  total_runs: number;
  completed_runs: number;
  failed_runs: number;
  avg_latency_ms: number | null;
  avg_score: number | null;
  avg_token_usage: number | null;
  created_at: string;
  prompt_stack_id: string | null;
  judge_prompt_stack_id: string | null;
  ontology_stack_id: string | null;
  metrics_profile_id: string | null;
  composed_system_prompt: string | null;
  scored_surface: string | null;
  // Frozen snapshot of the resolved model at launch — authoritative for
  // "which model produced this result", survives PromptStack / registry edits
  // (the live stack below only reflects what the app serves today).
  llm_model: string | null;
  judge_llm_model: string | null;
}

interface ExperimentTools {
  promptStack: {
    id: string;
    title: string;
    llm_app_key: string | null;
    model: string | null;
    temperature: number | null;
    few_shot_strategy: string | null;
    enable_thinking: boolean | null;
  } | null;
  judgePromptStack: {
    id: string;
    title: string;
    llm_app_key: string | null;
    model: string | null;
  } | null;
  ontologyStack: { id: string; title: string; field_count?: number } | null;
  metricsProfile: {
    id: string;
    name: string;
    entry_count?: number;
    is_system?: boolean;
    deleted_at?: string | null;
  } | null;
}

/** The dataset an experiment runs on, as far as this page branches on it. */
interface DatasetInfo {
  state: DatasetState;
  specialite: string | null;
}

/** A sealed reference a completed work experiment may be confirmed on. */
interface SealedReferenceOption {
  id: string;
  name: string;
}

export default function ExperimentDetailPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { id: datasetId, expId } = useParams();
  const [searchParams] = useSearchParams();
  const labReadOnly = useLabReadOnly();
  const [experiment, setExperiment] = useState<Experiment | null>(null);
  const [runs, setRuns] = useState<ExperimentRun[]>([]);
  const [metrics, setMetrics] = useState<ExperimentMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<GroundingField[]>([]);
  const [feedbackRunId, setFeedbackRunId] = useState<string | null>(null);
  const [validationRunId, setValidationRunId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [tools, setTools] = useState<ExperimentTools>({
    promptStack: null,
    judgePromptStack: null,
    ontologyStack: null,
    metricsProfile: null,
  });

  const [datasetInfo, setDatasetInfo] = useState<DatasetInfo | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [references, setReferences] = useState<SealedReferenceOption[] | null>(null);
  const [chosenReference, setChosenReference] = useState("");
  const [confirming, setConfirming] = useState(false);
  // A sealed reference's runs name its dossiers: the API refuses them, so they are not asked for.
  const sealed = datasetInfo?.state === "sealed";

  const loadDatasetInfo = async (): Promise<DatasetInfo | null> => {
    const response = await API.get({ path: `/dataset/${datasetId}` });
    if (!response?.ok || !response.data) return null;
    const info: DatasetInfo = {
      state: (response.data.state as DatasetState | undefined) ?? "work",
      specialite: (response.data.specialite as string | null | undefined) ?? null,
    };
    setDatasetInfo(info);
    return info;
  };

  useEffect(() => {
    if (datasetId && expId) {
      void (async () => {
        const info = await loadDatasetInfo();
        loadExperiment();
        loadMetrics();
        loadFields();
        if (info?.state === "sealed") setLoading(false);
        else loadRuns();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when route params change
  }, [datasetId, expId]);

  const openConfirm = async () => {
    setConfirmOpen(true);
    setReferences(null);
    setChosenReference("");
    const response = await API.get({ path: "/dataset/", query: { limit: 1000 } });
    const rows = (response?.ok ? response.data?.datasets : []) as Array<{
      id: string;
      name: string;
      state?: DatasetState;
      specialite?: string | null;
    }>;
    setReferences(
      (rows ?? [])
        .filter(
          (row) =>
            row.state === "sealed" &&
            (row.specialite ?? null) === (datasetInfo?.specialite ?? null),
        )
        .map((row) => ({ id: row.id, name: row.name })),
    );
  };

  const handleConfirmOnReference = async () => {
    if (!chosenReference || !expId) return;
    setConfirming(true);
    try {
      const response = await API.post({
        path: `/dataset/${chosenReference}/passages`,
        body: { candidate_experiment_id: expId },
      });
      if (response?.ok && response.data?.id) {
        navigate(`/datasets/${chosenReference}/experiments/${response.data.id}`);
        return;
      }
      toast.error(
        readRefusal(response)?.message ?? t`Le passage n'a pas pu être créé.`,
      );
    } finally {
      setConfirming(false);
    }
  };

  // Poll for status if running
  useEffect(() => {
    if (experiment?.status === "RUNNING") {
      const interval = setInterval(() => {
        loadExperiment();
        if (!sealed) loadRuns();
      }, 3000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadExperiment/loadRuns are stable, run when status changes
  }, [experiment?.status]);

  const loadExperiment = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${datasetId}/experiments/${expId}`,
      });
      if (response.ok) {
        setExperiment(response.data);
        loadTools(response.data);
      } else {
        setError(response.error || t`Erreur lors du chargement`);
      }
    } catch {
      setError(t`Erreur lors du chargement de l'expérience`);
    }
  };

  /**
   * Resolve the tools the experiment is bound to — PromptStack, OntologyStack
   * and MetricsProfile — so the Outils tab can show what configuration
   * actually drove this run. The composed_system_prompt is stored on the
   * experiment itself (phase 3a snapshot) and is the source of truth for
   * what was sent to the model.
   */
  const loadTools = async (exp: Experiment) => {
    const next: ExperimentTools = {
      promptStack: null,
      judgePromptStack: null,
      ontologyStack: null,
      metricsProfile: null,
    };
    const calls: Promise<unknown>[] = [];
    if (exp.prompt_stack_id) {
      calls.push(
        API.get({ path: `/prompt-stack/${exp.prompt_stack_id}` }).then((res) => {
          if (res.ok && res.data?.promptStack) next.promptStack = res.data.promptStack;
        }),
      );
    }
    if (exp.judge_prompt_stack_id) {
      calls.push(
        API.get({ path: `/prompt-stack/${exp.judge_prompt_stack_id}` }).then((res) => {
          if (res.ok && res.data?.promptStack) next.judgePromptStack = res.data.promptStack;
        }),
      );
    }
    if (exp.ontology_stack_id) {
      calls.push(
        API.get({ path: `/ontology-stack/${exp.ontology_stack_id}` }).then((res) => {
          if (res.ok && res.data) next.ontologyStack = res.data;
        }),
      );
    }
    if (exp.metrics_profile_id) {
      calls.push(
        // Deleted or not: it is still the profile this run was scored with.
        API.get({
          path: `/metrics-profile/${exp.metrics_profile_id}`,
          query: { include_deleted: true },
        }).then((res) => {
          if (res.ok && res.data) next.metricsProfile = res.data;
        }),
      );
    }
    await Promise.allSettled(calls);
    setTools(next);
  };

  const loadRuns = async () => {
    try {
      setLoading(true);
      const response = await API.get({
        path: `/dataset/${datasetId}/experiments/${expId}/runs`,
        query: { limit: 200 },
      });
      if (response.ok) {
        setRuns(response.data.runs || []);
      }
    } catch (e) {
      console.error("Error loading runs:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${datasetId}/experiments/${expId}/metrics`,
      });
      if (response.ok) {
        setMetrics(response.data.metrics);
      }
    } catch (e) {
      console.error("Error loading metrics:", e);
    }
  };

  const loadFields = async () => {
    try {
      const response = await API.get({
        path: `/dataset/${datasetId}/ontology`,
      });
      if (response.ok) {
        const raw = response.data?.fields ?? [];
        setFields(
          (raw as Array<{ field_key?: string; name?: string; group_name?: string | null }>)
            .filter((f) => f.field_key)
            .map((f) => ({
              field_key: f.field_key!,
              name: f.name ?? f.field_key!,
              group_name: f.group_name ?? null,
            }))
        );
      }
    } catch {
      // Non-blocking — grounding viewer works without field names
    }
  };

  const handleStart = async () => {
    setActionLoading(true);
    try {
      const response = await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/start`,
      });
      if (response.ok) {
        setExperiment(response.data);
        loadRuns();
        loadMetrics();
      } else {
        setError(response.error || t`Erreur lors du démarrage`);
      }
    } catch {
      setError(t`Erreur lors du démarrage`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const response = await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/cancel`,
      });
      if (response.ok) {
        setExperiment(response.data);
      }
    } catch {
      setError(t`Erreur lors de l'annulation`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunEvaluators = async () => {
    setActionLoading(true);
    try {
      const response = await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/evaluate`,
        body: {
          evaluators: [
            {
              type: "heuristic",
              key: "json_validity",
              config: { evaluator: "json_validity" },
            },
            {
              type: "heuristic",
              key: "output_length",
              config: {
                evaluator: "output_length",
                min_length: 10,
                max_length: 5000,
              },
            },
          ],
        },
      });
      if (response.ok) {
        loadRuns();
        loadMetrics();
        loadExperiment();
      }
    } catch (e) {
      console.error("Error running evaluators:", e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunLLMJudge = async () => {
    setActionLoading(true);
    try {
      const response = await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/evaluate`,
        body: {
          evaluators: [
            {
              type: "llm_judge",
              key: "semantic_match",
              config: {},
            },
          ],
        },
      });
      if (response.ok) {
        loadRuns();
        loadMetrics();
        loadExperiment();
      }
    } catch (e) {
      console.error("Error running LLM judge:", e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddFeedback = (runId: string) => {
    setFeedbackRunId(runId);
  };

  const handleValidate = (runId: string) => {
    setValidationRunId(runId);
  };

  const handleValidationSubmit = async (
    corrections: Record<string, unknown>,
    validatedFields: string[],
  ) => {
    if (!validationRunId) return;
    try {
      await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/runs/${validationRunId}/validate`,
        body: { corrections, validated_fields: validatedFields, add_shots: true },
      });
      loadRuns();
      loadMetrics();
      loadExperiment();
    } catch (e) {
      console.error("Error validating run:", e);
    }
  };

  const handleFeedbackSubmit = async (data: {
    key: string;
    score: number | null;
    value: string | null;
    comment: string | null;
    source_type: string;
  }) => {
    if (!feedbackRunId) return;
    try {
      await API.post({
        path: `/dataset/${datasetId}/experiments/${expId}/runs/${feedbackRunId}/feedback`,
        body: data,
      });
      loadRuns();
      loadMetrics();
      loadExperiment();
    } catch (e) {
      console.error("Error adding feedback:", e);
    }
  };

  if (loading && !experiment) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-slate-500"><Trans>Chargement...</Trans></p>
        </div>
      </div>
    );
  }

  if (error && !experiment) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!experiment) return null;

  // A flat measurement is kept for reading only: the server refuses to run it (409).
  const retiredSurface = experiment.scored_surface !== "PROJECTION";
  const canStart =
    !retiredSurface &&
    ["PENDING", "FAILED", "CANCELLED"].includes(experiment.status);
  const canCancel = experiment.status === "RUNNING";
  // Annotating is a write: a read-only session has no such tab to open.
  const requestedTab = EXPERIMENT_TABS.find(
    (tab) => tab === searchParams.get("tab"),
  );
  const openedTab = sealed
    ? requestedTab === "tools"
      ? "tools"
      : "metrics"
    : requestedTab && !(labReadOnly && requestedTab === "annotate")
      ? requestedTab
      : "results";
  // A completed work experiment is what a sealed reference measures: confirming it starts a passage.
  const canConfirm =
    !labReadOnly &&
    !retiredSurface &&
    datasetInfo?.state === "work" &&
    experiment.status === "COMPLETED";

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/datasets/${datasetId}`}
          className="text-sm text-primary hover:underline"
        >
          <Trans>← Retour au jeu de données</Trans>
        </Link>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="text-3xl font-bold">{experiment.name}</h1>
          <ExperimentStatusBadge status={experiment.status} />
          <DatasetStateBadge state={datasetInfo?.state} />
          {retiredSurface && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
              <Trans>Agrégation à plat (retirée)</Trans>
            </span>
          )}
        </div>
        {experiment.description && (
          <p className="text-slate-600 mt-1">{experiment.description}</p>
        )}

        {/* Action buttons */}
        {!labReadOnly && (
          <div className="flex gap-2 mt-3">
            {canStart && (
              <Button onClick={handleStart} disabled={actionLoading}>
                <Play className="h-4 w-4 mr-1" />
                {actionLoading ? t`Démarrage...` : t`Démarrer`}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={actionLoading}
              >
                <Square className="h-4 w-4 mr-1" />
                <Trans>Annuler</Trans>
              </Button>
            )}
            {experiment.status === "COMPLETED" && !sealed && (
              <Button
                variant="outline"
                onClick={handleRunEvaluators}
                disabled={actionLoading}
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                {actionLoading ? t`Évaluation...` : t`Évaluer automatiquement`}
              </Button>
            )}
            {experiment.status === "COMPLETED" && !sealed && (
              <Button
                variant="outline"
                onClick={handleRunLLMJudge}
                disabled={actionLoading}
              >
                <Brain className="h-4 w-4 mr-1" />
                {actionLoading ? t`Évaluation...` : t`Évaluer avec le LLM`}
              </Button>
            )}
            {canConfirm && (
              <Button onClick={() => void openConfirm()}>
                <Lock className="h-4 w-4 mr-1" />
                <Trans>Confirmer sur une référence scellée</Trans>
              </Button>
            )}
          </div>
        )}

        {error && (
          <div className="mt-3 bg-red-50 text-red-700 p-3 rounded text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue={openedTab}>
        <div className="flex flex-wrap items-center gap-3">
          <TabsList>
            {!sealed && (
              <TabsTrigger value="results"><Trans>Résultats ({runs.length})</Trans></TabsTrigger>
            )}
            <TabsTrigger value="metrics"><Trans>Métriques</Trans></TabsTrigger>
            {!labReadOnly && !sealed && (
              <TabsTrigger value="annotate">
                <Trans>Annoter</Trans>
              </TabsTrigger>
            )}
            <TabsTrigger value="tools"><Trans>Outils</Trans></TabsTrigger>
          </TabsList>
          {sealed && (
            <span className="text-sm text-slate-500">
              <Trans>
                Jeu de référence scellé : ni résultats par dossier, ni annotation, ni
                détail par champ.
              </Trans>
            </span>
          )}
        </div>

        <TabsContent value="results" className="mt-4">
          <Card>
            <CardContent className="pt-4">
              <RunsTable
                runs={runs}
                fields={fields}
                onAddFeedback={labReadOnly ? undefined : handleAddFeedback}
                onValidate={labReadOnly ? undefined : handleValidate}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="mt-4">
          {metrics ? (
            <ExperimentMetricsPanel metrics={metrics} />
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p><Trans>Pas encore de métriques disponibles.</Trans></p>
              <p className="text-sm mt-1">
                <Trans>Démarrez l'expérience et ajoutez des évaluations pour voir les métriques.</Trans>
              </p>
            </div>
          )}
        </TabsContent>

        {!labReadOnly && (
          <TabsContent value="annotate" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base"><Trans>Annotation manuelle</Trans></CardTitle>
                <p className="text-sm text-slate-500">
                  <Trans>Cliquez sur l'icône de retour pour annoter chaque run individuellement.</Trans>
                </p>
              </CardHeader>
              <CardContent>
                <RunsTable runs={runs} fields={fields} onAddFeedback={handleAddFeedback} onValidate={handleValidate} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="tools" className="mt-4">
          <ExperimentToolsPanel
            experiment={experiment}
            tools={tools}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              <Trans>Confirmer sur une référence scellée</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>
                La configuration figée de cette expérience (stacks, profil de métriques)
                sera évaluée sur la référence choisie. Le passage sera inscrit à son
                registre et ne pourra pas être retiré ; seul son score agrégé sera
                affiché.
              </Trans>
            </DialogDescription>
          </DialogHeader>
          {references === null ? (
            <p className="py-4 text-sm text-slate-500">
              <Trans>Chargement...</Trans>
            </p>
          ) : references.length === 0 ? (
            <p className="py-4 text-sm text-slate-500">
              <Trans>Aucune référence scellée dans cette spécialité.</Trans>
            </p>
          ) : (
            <RadioGroup
              value={chosenReference}
              onValueChange={setChosenReference}
              className="space-y-2"
            >
              {references.map((reference) => (
                <Label
                  key={reference.id}
                  htmlFor={`reference-${reference.id}`}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3 font-normal hover:border-primary/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                >
                  <RadioGroupItem id={`reference-${reference.id}`} value={reference.id} />
                  <span className="font-semibold text-slate-900">{reference.name}</span>
                </Label>
              ))}
            </RadioGroup>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              <Trans>Annuler</Trans>
            </Button>
            <Button
              disabled={!chosenReference || confirming}
              onClick={() => void handleConfirmOnReference()}
            >
              {confirming ? t`Création...` : t`Créer le passage`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback dialog */}
      <FeedbackDialog
        open={feedbackRunId !== null}
        onClose={() => setFeedbackRunId(null)}
        onSubmit={handleFeedbackSubmit}
      />

      {/* Validation dialog */}
      <ValidationDialog
        open={validationRunId !== null}
        onClose={() => setValidationRunId(null)}
        run={runs.find((r) => r.id === validationRunId) ?? null}
        onSubmit={handleValidationSubmit}
      />
    </div>
  );
}

/**
 * Read-only summary of the tools an experiment is bound to:
 * - PromptStack (LLM provider/model/temperature/few-shot strategy)
 * - OntologyStack (the schema used to build the extraction prompt)
 * - MetricsProfile (the scoring rubric)
 * - composed_system_prompt (phase 3a frozen snapshot — what was actually
 *   sent to the model)
 *
 * Nothing is editable here — the experiment is meant to be reproducible.
 * To change tools, create a new experiment.
 */
function ExperimentToolsPanel({
  experiment,
  tools,
}: {
  experiment: Experiment | null;
  tools: ExperimentTools;
}) {
  const { t, i18n } = useLingui();
  if (!experiment) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-slate-500">
          <Trans>Chargement…</Trans>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* LLM (PromptStack) */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 pb-3 space-y-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Cpu className="h-5 w-5" />
          </div>
          <CardTitle className="text-base">
            <Trans>Configuration LLM</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {tools.promptStack || experiment.llm_model ? (
            <div className="space-y-3">
              {tools.promptStack && (
                <div className="font-medium text-slate-900">
                  {tools.promptStack.title}
                </div>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {tools.promptStack?.llm_app_key && (
                  <Badge variant="outline" className="text-xs">
                    {tools.promptStack.llm_app_key}
                  </Badge>
                )}
                {(experiment.llm_model || tools.promptStack?.model) && (
                  <Badge
                    variant="outline"
                    className="text-xs font-mono"
                    title={t`Modèle figé au lancement de l'expérience`}
                  >
                    {experiment.llm_model || tools.promptStack?.model}
                  </Badge>
                )}
                {tools.promptStack && tools.promptStack.temperature !== null && (
                  <Badge variant="outline" className="text-xs">
                    <Trans>température</Trans>: {tools.promptStack.temperature}
                  </Badge>
                )}
                {tools.promptStack?.few_shot_strategy && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-blue-50 text-blue-900 border-blue-200"
                  >
                    <Trans>shots</Trans>: {tools.promptStack.few_shot_strategy}
                  </Badge>
                )}
                {tools.promptStack && tools.promptStack.enable_thinking !== null && (
                  <Badge variant="outline" className="text-xs">
                    <Trans>raisonnement</Trans>:{" "}
                    {tools.promptStack.enable_thinking ? "on" : "off"}
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              <Trans>Aucune configuration LLM associée.</Trans>
            </p>
          )}
          {/* Shown on its own: a projection run has a judge but no extraction model. */}
          {(tools.judgePromptStack || experiment.judge_llm_model) && (
            <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
              <span className="font-medium">
                <Trans>Juge LLM</Trans>:
              </span>{" "}
              {tools.judgePromptStack?.title}{" "}
              {(experiment.judge_llm_model ||
                tools.judgePromptStack?.model) && (
                <span className="font-mono">
                  (
                  {experiment.judge_llm_model ||
                    tools.judgePromptStack?.model}
                  )
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ontology */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 pb-3 space-y-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Network className="h-5 w-5" />
          </div>
          <CardTitle className="text-base">
            <Trans>Ontologie</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {tools.ontologyStack ? (
            <div className="space-y-1">
              <div className="font-medium text-slate-900">
                {tools.ontologyStack.title}
              </div>
              {tools.ontologyStack.field_count !== undefined && (
                <p className="text-xs text-slate-500">
                  <Trans>{tools.ontologyStack.field_count} champs</Trans>
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">
              <Trans>Champs du jeu de données (pas de stack dédiée).</Trans>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Metrics */}
      <Card>
        <CardHeader className="flex flex-row items-center gap-3 pb-3 space-y-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Gauge className="h-5 w-5" />
          </div>
          <CardTitle className="text-base">
            <Trans>Profil de métriques</Trans>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {tools.metricsProfile ? (
            <div className="space-y-1">
              <div className="font-medium text-slate-900 flex items-center gap-2">
                {tools.metricsProfile.name}
                {tools.metricsProfile.is_system && (
                  <Badge variant="outline" className="text-xs">
                    <Trans>Système</Trans>
                  </Badge>
                )}
              </div>
              {tools.metricsProfile.entry_count !== undefined && (
                <p className="text-xs text-slate-500">
                  <Trans>{tools.metricsProfile.entry_count} champs scorés</Trans>
                </p>
              )}
              {tools.metricsProfile.deleted_at && (
                <p className="text-sm text-slate-500">
                  <Trans>
                    Profil supprimé le{" "}
                    {new Date(tools.metricsProfile.deleted_at).toLocaleDateString(i18n.locale)}{" "}
                    : le détail par champ ci-dessous reste celui de ce profil.
                  </Trans>
                </p>
              )}
            </div>
          ) : experiment.metrics_profile_id ? (
            // The run was scored with a profile that no longer resolves: say so rather than
            // claim the default contract, which this run was not scored with.
            <p className="text-sm text-slate-500">
              <Trans>
                Scorée avec un profil de métriques supprimé depuis : le détail par
                champ ci-dessous reste celui de ce profil.
              </Trans>
            </p>
          ) : (
            <p className="text-sm text-slate-500">
              <Trans>
                Contrat par défaut : l'évaluateur et le poids déclarés dans
                l'ontologie de la spécialité.
              </Trans>{" "}
              <Link
                to="/laboratoire/metriques"
                className="text-primary hover:underline"
              >
                <Trans>Voir le contrat</Trans>
              </Link>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Composed system prompt — phase 3a snapshot */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            <Trans>Prompt système figé</Trans>
          </CardTitle>
          <p className="text-xs text-slate-500">
            <Trans>
              Capturé à la création de l'expérience. Verrouille la
              reproductibilité contre toute modification ultérieure des
              ressources sources.
            </Trans>
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          {experiment.composed_system_prompt ? (
            <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded p-3 max-h-[480px] overflow-auto text-slate-700">
              {experiment.composed_system_prompt}
            </pre>
          ) : experiment.scored_surface === "PROJECTION" ? (
            <p className="text-sm text-slate-400">
              <Trans>
                Aucun prompt n'est envoyé : l'expérience relit la projection
                datée de chaque dossier, telle que la lecture des documents l'a
                écrite.
              </Trans>
            </p>
          ) : (
            <p className="text-sm text-slate-400">
              <Trans>
                Aucun snapshot — l'expérience utilise le builder dynamique au
                runtime.
              </Trans>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
